import type { GroupListItem } from '@doneverse/shared'
import { Compass } from 'lucide-react'
import { useMemo } from 'react'
import { CollectionSectionBlock, type CollectionSectionItem } from './CollectionSectionBlock'
import { CreateGroupDialog } from './GroupAdminDialogs'
import { GroupsInboxLinks, type GroupsInboxView } from './GroupsInboxLinks'
import { Skeleton } from '@/components/ui/skeleton'

interface Props {
  /**
   * Admins browse every group (and can create new ones from the rail) but don't
   * get the personal mentions/replies inbox links.
   */
  isAdmin?: boolean
  activeView: GroupsInboxView | null
  activeGroupId: string | null
  myGroups: GroupListItem[]
  discoverGroups: GroupListItem[]
  isLoading: boolean
}

/**
 * The single, persistent left rail for every `/groups*` page. Lives in the
 * shared layout route so it never unmounts when navigating between a group,
 * mentions, or replies — the list is identical everywhere because it's fed by
 * the stable "mine" + "discover" queries, not the active group's collection.
 */
export function GroupsSidebar({
  isAdmin = false,
  activeView,
  activeGroupId,
  myGroups,
  discoverGroups,
  isLoading,
}: Props) {
  const byCollection = useMemo(() => groupByCollection(myGroups), [myGroups])
  // Don't repeat groups you've already joined in the Discover section.
  const discover = useMemo(() => discoverGroups.filter((g) => !g.viewer_is_member), [discoverGroups])

  return (
    <nav className="space-y-4 px-3 py-3 text-sm">
      {isAdmin ? null : <GroupsInboxLinks activeView={activeView} />}

      {isAdmin ? (
        <div className="px-1">
          <CreateGroupDialog />
        </div>
      ) : null}

      {isLoading ? (
        <GroupsSidebarSkeleton />
      ) : (
        <>
          {byCollection.map((section) => (
            <CollectionSectionBlock
              key={section.key}
              label={section.label}
              activeId={activeGroupId ?? undefined}
              items={section.items}
            />
          ))}

          {discover.length > 0 ? (
            <CollectionSectionBlock
              label="Discover"
              activeId={activeGroupId ?? undefined}
              items={discover.map((g) => ({ id: g.id, label: g.name, icon: g.icon ?? null }))}
              fallbackIcon={<Compass className="h-3.5 w-3.5" />}
            />
          ) : null}
        </>
      )}
    </nav>
  )
}

export function GroupsSidebarSkeleton() {
  return (
    <div className="space-y-4" role="status" aria-label="Loading spaces">
      {[0, 1].map((section) => (
        <div key={section} className="space-y-2">
          <Skeleton className="ml-3 h-3 w-28 rounded" />
          <div className="space-y-1">
            {[0, 1, 2].map((row) => (
              <div key={row} className="flex items-center gap-2 px-2 py-1.5">
                <Skeleton className="h-[34px] w-[34px] shrink-0 rounded-[17px]" />
                <Skeleton className="h-4 flex-1 rounded" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

interface CollectionSection {
  key: string
  label: string
  items: CollectionSectionItem[]
}

function groupByCollection(groups: GroupListItem[]): CollectionSection[] {
  const map = new Map<string, CollectionSection>()
  for (const g of groups) {
    const key = g.collection?.id ?? '__no_collection__'
    const label = g.collection?.name ?? 'Spaces'
    const section = map.get(key) ?? { key, label, items: [] }
    section.items.push({ id: g.id, label: g.name, icon: g.icon })
    map.set(key, section)
  }
  return Array.from(map.values())
}
